import { BaseRemoteService } from "@ofqual/portal-core/core/dataAccess";
import {
  Opts,
  Model,
  QueryParams,
  PagedResponse,
  ValidatedResponse,
  PatchWithMeta,
} from "@ofqual/portal-core/core/types";
import { paramsToSearchString } from "@ofqual/portal-core/core/utils/urlHelper";

import { PagedResponseTo } from "@ofqual/portal-core/core/utils/pagedResponseHelpers";
import {
  PublicEnquiryModel,
  fromApiModel,
  PublicEnquiryResponse,
} from "../domain/PublicEnquiry";
import { getValidationError } from "@ofqual/portal-core/core/utils/validationHelper";

export class PublicEnquiryService extends BaseRemoteService {
  public async get(id: string, opts: Opts): Promise<PublicEnquiryModel> {
    const url = `${this.apiRoot}/publicEnquiries/${id}`;
    const response = await super.getRequest<Model>(url, opts);
    return fromApiModel(response);
  }

  public async search(
    params: QueryParams,
    opts: Opts
  ): Promise<PagedResponse<PublicEnquiryModel>> {
    const url = `${this.apiRoot}/publicEnquiries/${paramsToSearchString(
      params
    )}`;
    const response = await super.getRequest<PagedResponse<Model>>(url, opts);
    return PagedResponseTo<PublicEnquiryModel>(response, fromApiModel);
  }

  public async update(
    id: string,
    patchDocument: PatchWithMeta<PublicEnquiryModel>,
    opts: Opts
  ): Promise<ValidatedResponse<PublicEnquiryModel>> {
    const url = `${this.apiRoot}/publicEnquiries/${id}`;
    //console.log(patchDocument);
    try {
      const response = await this.patchRequest<
        PublicEnquiryModel,
        PatchWithMeta<PublicEnquiryModel>
      >(url, patchDocument, opts);
      return {
        isValid: true,
        data: fromApiModel(response),
      };
    } catch (err) {
      const validationError = getValidationError(err);
      if (validationError.isValidationError) {
        return { isValid: false, error: validationError.error };
      }
      throw err;
    }
  }

  public async create(
    item: PublicEnquiryModel,
    opts: Opts
  ): Promise<ValidatedResponse<PublicEnquiryModel>> {
    const url = `${this.apiRoot}/publicenquiries`;

    //const peSerialised = JSON.stringify(item);

    try {
      const response: PublicEnquiryModel = await super.postRequest(
        url,
        item,
        opts
      );
      return {
        isValid: true,
        data: response,
      };
    } catch (err) {
      const validationError = getValidationError(err);
      if (validationError.isValidationError) {
        return { isValid: false, error: validationError.error };
      }
      throw err;
    }
  }

  public async addCaseNote(
    id: string,
    caseFiles: File[],
    caseNote: string,
    sla_ResponseType: number,
    subject: string,
    opts: Opts
  ): Promise<unknown> {
    let url = `${this.apiRoot}/publicenquiries/${id}/casenote`;

    if (sla_ResponseType === PublicEnquiryResponse.ResponseCustomer) {
      const messageData = { id, subject, message: caseNote };
      const messageSerialized = JSON.stringify(messageData);
      url = `${this.apiRoot}/publicenquiries/${id}/public-message-ofqual`;
      return await super.postRequestFormData(
        url,
        {
          messageSerialized,
          files: caseFiles,
        },
        opts
      );
    }

    return await super.postRequestFormData(
      url,
      { caseNote, caseFiles, sla_ResponseType, subject },
      opts
    );
  }

  public async addInternalMessage(
    id: string,
    files: File[],
    subject: string,
    message: string,
    recipients: string[],
    opts: Opts
  ): Promise<unknown> {
    const url = `${this.apiRoot}/publicenquiries/${id}/internal-message`;
    const messageData = { id, subject, message, recipients };
    const messageSerialized = JSON.stringify(messageData);
    return await super.postRequestFormData(
      url,
      { messageSerialized, files },
      opts
    );
  }

  public async downloadActivityAttachment(
    id: string,
    activityId: string,
    blobId: string,
    opts: Opts
  ): Promise<Blob> {
    const url = `${this.apiRoot}/publicenquiries/${id}/documents/${activityId}/download/${blobId}`;
    return await super.getRequestBlob(url, opts);
  }

  public async assign(
    id: string,
    assignedToUpn: string,
    assignedToDisplayName: string,
    opts: Opts
  ) {
    const url = `${this.apiRoot}/publicenquiries/${id}/assign`;
    return await super.postRequest(
      url,
      { assignedToUpn, assignedToDisplayName },
      opts
    );
  }
  public async unassign(id: string, opts: Opts) {
    const url = `${this.apiRoot}/publicenquiries/${id}/unassign`;
    return await super.postRequest(url, null, opts);
  }

  public async closeCase(id: string, opts: Opts): Promise<PublicEnquiryModel> {
    const url = `${this.apiRoot}/publicenquiries/${id}/close`;
    const response = await super.postRequest<PublicEnquiryModel>(
      url,
      null,
      opts
    );

    return response;
  }

  public async reopenCase(id: string, opts: Opts): Promise<PublicEnquiryModel> {
    const url = `${this.apiRoot}/publicenquiries/${id}/open`;
    const response = await super.postRequest<PublicEnquiryModel>(
      url,
      null,
      opts
    );

    return response;
  }

  public async setPendingLines(
    id: string,
    pendingLines: boolean,
    opts: Opts
  ): Promise<PublicEnquiryModel> {
    const url = `${this.apiRoot}/publicenquiries/${id}/pendingLines/${pendingLines}`;
    const response = await super.postRequest<PublicEnquiryModel>(
      url,
      null,
      opts
    );

    return response;
  }

  public async downloadSupportingDocument(
    id: string,
    blobId: string,
    opts: Opts
  ) {
    const url = `${this.apiRoot}/publicenquiries/${id}/supportingdocument/${blobId}/download`;
    return await super.getRequestBlob(url, opts);
  }

  public async deleteSupportingDocument(
    id: string,
    blobId: string,
    opts: Opts
  ): Promise<unknown> {
    const url = `${this.apiRoot}/publicenquiries/${id}/supportingdocument/${blobId}`;
    return await super.deleteRequest(url, opts);
  }

  public async postSupportingDocument(
    id: string,
    attachments: File[],
    opts: Opts
  ): Promise<unknown> {
    const url = `${this.apiRoot}/publicenquiries/${id}/supportingdocuments`;
    return await super.postRequestFormData(url, { files: attachments }, opts);
  }
}
